import React, { useState, useEffect } from 'react';
import { collection, getDocs, query, updateDoc, doc, deleteDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { DataGrid } from '@mui/x-data-grid';
import {
  Box,
  Typography,
  Button,
  Modal,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Container,
} from '@mui/material';
import { styled } from '@mui/system';
import { useNavigate } from 'react-router-dom';
import LocationCityIcon from '@mui/icons-material/LocationCity';
import DeleteIcon from '@mui/icons-material/Delete'; // Added DeleteIcon

// Modern color palette
const colors = {
  primary: '#2ecc71',
  secondary: '#34495e',
  background: '#f5f7fa',
  accent: '#3498db',
  gradientStart: 'rgba(62,180,137,1)',
  gradientEnd: 'rgba(144,238,144,1)',
};

// Styled components
const ModernCard = styled(Box)(({ theme }) => ({
  borderRadius: '16px',
  background: 'white',
  border: '1px solid rgba(0, 0, 0, 0.05)',
  boxShadow: '0 6px 18px rgba(0, 0, 0, 0.06)',
  padding: theme.spacing(2),
  transition: 'transform 0.2s ease, box-shadow 0.2s ease',
  '&:hover': {
    transform: 'translateY(-6px)',
    boxShadow: '0 12px 24px rgba(0, 0, 0, 0.1)',
  },
}));

const AnimatedButton = styled(Button)(({ theme }) => ({
  borderRadius: '10px',
  textTransform: 'none',
  fontWeight: '600',
  padding: theme.spacing(1.25, 2.5),
  transition: 'all 0.3s ease',
  '&:hover': {
    backgroundColor: colors.accent,
    color: 'white',
  },
}));

const DeleteButton = styled(Button)(({ theme }) => ({
  borderRadius: '10px',
  textTransform: 'none',
  fontWeight: '600',
  padding: theme.spacing(1, 2),
  color: '#e74c3c',
  borderColor: '#e74c3c',
  '&:hover': {
    backgroundColor: '#e74c3c',
    color: 'white',
    borderColor: '#e74c3c',
  },
}));

const ModalBox = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: { xs: '90%', sm: 450 },
  maxWidth: '100%',
  backgroundColor: 'white',
  borderRadius: { xs: '8px', sm: '12px' },
  boxShadow: '0 8px 24px rgba(0, 0, 0, 0.2)',
  padding: { xs: theme.spacing(2), sm: theme.spacing(4) },
  maxHeight: '90vh',
  overflowY: 'auto',
}));

const SchoolLevels = () => {
  const [schoolLevels, setSchoolLevels] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [selectedSchoolLevel, setSelectedSchoolLevel] = useState(null);
  const navigate = useNavigate();

  const colorOptions = [
    { value: '#ffffff', label: 'White' },
    { value: '#f8d7da', label: 'Light Red' },
    { value: '#d4edda', label: 'Light Green' },
    { value: '#cce5ff', label: 'Light Blue' },
    { value: '#fff3cd', label: 'Light Yellow' },
    { value: '#e2e3e5', label: 'Light Gray' },
    { value: '#f5c6cb', label: 'Soft Pink' },
    { value: '#c3e6cb', label: 'Mint Green' },
    { value: '#b8daff', label: 'Sky Blue' },
    { value: '#ffeeba', label: 'Pale Yellow' },
    { value: '#d6bcfa', label: 'Lavender' },
    { value: '#fdd0e0', label: 'Baby Pink' },
    { value: '#b5f5e2', label: 'Aqua' },
    { value: '#fed7aa', label: 'Peach' },
    { value: '#e9d8fd', label: 'Lilac' },
    { value: '#a3bffa', label: 'Periwinkle' },
    { value: '#fed7d7', label: 'Coral' },
    { value: '#bef264', label: 'Lime' },
    { value: '#bbf7d0', label: 'Pistachio' },
    { value: '#bfdbfe', label: 'Cornflower Blue' },
  ];

  useEffect(() => {
    const fetchSchoolLevels = async () => {
      setIsLoading(true);
      const schoolLevelsCollection = collection(db, 'schoolLevels');
      const schoolLevelsQuery = query(schoolLevelsCollection);

      try {
        const querySnapshot = await getDocs(schoolLevelsQuery);
        const schoolLevelsData = querySnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setSchoolLevels(schoolLevelsData);
      } catch (error) {
        console.error('Error fetching school levels:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchSchoolLevels();
  }, []);

  const filteredSchoolLevels = schoolLevels.filter((schoolLevel) =>
    schoolLevel.name.toLowerCase().includes(searchText.toLowerCase()) ||
    schoolLevel.description.toLowerCase().includes(searchText.toLowerCase())
  );

  const handleEditClick = (schoolLevel) => {
    setSelectedSchoolLevel(schoolLevel);
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setSelectedSchoolLevel(null);
  };

  const handleSaveSchoolLevel = async () => {
    if (!selectedSchoolLevel) return;

    try {
      const schoolLevelRef = doc(db, 'schoolLevels', selectedSchoolLevel.id);
      await updateDoc(schoolLevelRef, {
        name: selectedSchoolLevel.name,
        description: selectedSchoolLevel.description,
        backgroundColor: selectedSchoolLevel.backgroundColor,
      });

      setSchoolLevels(schoolLevels.map((level) =>
        level.id === selectedSchoolLevel.id ? selectedSchoolLevel : level
      ));
      handleCloseModal();
    } catch (error) {
      console.error('Error updating school level:', error);
    }
  };

  const handleDeleteSchoolLevel = async (schoolLevelId) => {
    if (window.confirm('Are you sure you want to delete this school level? This action cannot be undone.')) {
      try {
        const schoolLevelRef = doc(db, 'schoolLevels', schoolLevelId);
        await deleteDoc(schoolLevelRef);
        setSchoolLevels(schoolLevels.filter((level) => level.id !== schoolLevelId));
      } catch (error) {
        console.error('Error deleting school level:', error);
      }
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setSelectedSchoolLevel((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSearchChange = (e) => {
    setSearchText(e.target.value);
  };

  const columns = [
    { field: 'name', headerName: 'School Level', width: 250 },
    { field: 'description', headerName: 'Description', width: 750 },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 250, // Increased width to accommodate two buttons
      renderCell: (params) => (
        <Box sx={{ display: 'flex', gap: 1 }}>
          <AnimatedButton
            variant="outlined"
            color="primary"
            size="small"
            onClick={() => handleEditClick(params.row)}
          >
            Edit
          </AnimatedButton>
          <DeleteButton
            variant="outlined"
            size="small"
            startIcon={<DeleteIcon />}
            onClick={() => handleDeleteSchoolLevel(params.row.id)}
          >
            Delete
          </DeleteButton>
        </Box>
      ),
    },
  ];

  return (
    <div style={{ background: colors.background }}>
      <Container sx={{ py: 8, minHeight: '100vh', mt: 5 }}>
        {/* Header */}
        <Box
          sx={{
            mb: 5,
            p: 4,
            borderRadius: '16px',
            background: `linear-gradient(159deg, ${colors.gradientStart} 0%, ${colors.gradientEnd} 100%)`,
            color: 'white',
            boxShadow: '0 6px 18px rgba(0, 0, 0, 0.1)',
          }}
        >
          <Typography variant="h4" sx={{ fontWeight: '700', display: 'flex', alignItems: 'center' }}>
            <LocationCityIcon sx={{ fontSize: 35, mr: 1 }} />
            School Levels
          </Typography>
          <Typography variant="body1" sx={{ mt: 1, color: 'rgba(255, 255, 255, 0.9)' }}>
            Manage and edit school levels in the portal.
          </Typography>
        </Box>

        {/* School Levels Table */}
        <ModernCard>
          <Box sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Typography variant="h6" sx={{ color: colors.secondary, fontWeight: '600' }}>
                School Level List
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
                <AnimatedButton
                  variant="contained"
                  color="primary"
                  onClick={() => navigate('/addschoollevels')}
                >
                  Add Level
                </AnimatedButton>
                <TextField
                  label="Search School Levels"
                  variant="outlined"
                  value={searchText}
                  onChange={handleSearchChange}
                  placeholder="Search by name or description"
                  sx={{
                    width: { xs: '100%', sm: '200px' },
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '10px',
                      '& fieldset': { borderColor: colors.primary },
                      '&:hover fieldset': { borderColor: colors.accent },
                      '&.Mui-focused fieldset': { borderColor: colors.accent },
                    },
                    '& .MuiInputLabel-root': {
                      color: colors.secondary,
                      '&.Mui-focused': { color: colors.accent },
                    },
                  }}
                />
              </Box>
            </Box>
            <DataGrid
              rows={filteredSchoolLevels}
              columns={columns}
              pageSize={10}
              checkboxSelection={false}
              loading={isLoading}
              sx={{
                '& .MuiDataGrid-root': { border: 'none' },
                '& .MuiDataGrid-cell': { color: colors.secondary },
                '& .MuiDataGrid-columnHeaders': {
                  backgroundColor: colors.background,
                  color: colors.secondary,
                  fontWeight: '600',
                },
                '& .MuiDataGrid-row:hover': { backgroundColor: '#f0f4f8' },
              }}
            />
          </Box>
        </ModernCard>

        {/* Edit Modal */}
        <Modal open={openModal} onClose={handleCloseModal}>
          <ModalBox>
            <Typography
              variant="h5"
              sx={{ fontWeight: '700', color: colors.secondary, mb: 3 }}
            >
              Edit School Level
            </Typography>
            {selectedSchoolLevel && (
              <Box component="form">
                <TextField
                  fullWidth
                  label="School Level"
                  name="name"
                  value={selectedSchoolLevel.name}
                  onChange={handleInputChange}
                  sx={{ mb: 2 }}
                />
                <TextField
                  fullWidth
                  label="Description"
                  name="description"
                  value={selectedSchoolLevel.description}
                  onChange={handleInputChange}
                  sx={{ mb: 2 }}
                />
                <FormControl fullWidth sx={{ mb: 3 }}>
                  <InputLabel sx={{ color: colors.secondary }}>Background Color</InputLabel>
                  <Select
                    name="backgroundColor"
                    value={selectedSchoolLevel.backgroundColor}
                    onChange={handleInputChange}
                    sx={{
                      borderRadius: '10px',
                      '& .MuiOutlinedInput-notchedOutline': { borderColor: colors.primary },
                      '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: colors.accent },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: colors.accent },
                    }}
                  >
                    {colorOptions.map((color) => (
                      <MenuItem key={color.value} value={color.value}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Box
                            sx={{
                              width: 20,
                              height: 20,
                              backgroundColor: color.value,
                              mr: 1,
                              border: '1px solid #ccc',
                            }}
                          />
                          {color.label}
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <AnimatedButton
                    variant="contained"
                    color="primary"
                    onClick={handleSaveSchoolLevel}
                  >
                    Save
                  </AnimatedButton>
                  <AnimatedButton
                    variant="outlined"
                    color="secondary"
                    onClick={handleCloseModal}
                  >
                    Cancel
                  </AnimatedButton>
                </Box>
              </Box>
            )}
          </ModalBox>
        </Modal>
      </Container>
    </div>
  );
};

export default SchoolLevels;